import { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useConnectedAccountsDetail } from '../api/hooks';
import { analytics } from '../lib/analytics';

interface ConnectedAccountsStatusProps {
  memberId: string;
  compact?: boolean;
}

export function ConnectedAccountsStatus({ memberId, compact = false }: ConnectedAccountsStatusProps) {
  const { data: accountDetails, isLoading } = useConnectedAccountsDetail(memberId);

  const hasGoogle = accountDetails ? accountDetails.google.count > 0 : false;
  const hasMicrosoft = accountDetails ? accountDetails.microsoft.count > 0 : false;

  // Check if any accounts need reconnection
  const googleNeedsReconnect = accountDetails?.google.accounts.some(acc => acc.needsReconnect === true || acc.name === 'Reconnection required') ?? false;
  const microsoftNeedsReconnect = accountDetails?.microsoft.accounts.some(acc => acc.needsReconnect === true || acc.name === 'Reconnection required') ?? false;

  const handleReconnect = (provider: 'google' | 'microsoft', memberId: string) => {
    // Use CONNECT endpoint - requires authentication, updates existing user
    const params = new URLSearchParams();
    params.set('memberId', memberId);
    params.set('sourceType', provider);
    window.location.href = `/api/v1/connect/${provider}?${params.toString()}`;
  };

  // Track when connected accounts are viewed
  useEffect(() => {
    if (accountDetails) {
      analytics.connectedAccountsViewed(memberId, hasGoogle, hasMicrosoft);
    }
  }, [memberId, accountDetails, hasGoogle, hasMicrosoft]);

  if (isLoading) {
    return (
      <div className="mb-3 p-2 bg-bg-secondary rounded-lg">
        <div className="text-xs text-text-muted">Loading accounts...</div>
      </div>
    );
  }

  if (!accountDetails) {
    return null;
  }

  if (compact) {
    return (
      <div className="mb-3 p-3 bg-bg-secondary rounded-lg border border-border-light">
        <div className="text-xs font-medium text-text-primary mb-2">Connected Accounts</div>
        <div className="space-y-2">
          {/* Google Status */}
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded flex items-center justify-center bg-white flex-shrink-0">
              <svg className="w-3 h-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </div>
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              {hasGoogle ? (
                <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
              )}
              <span className={`text-xs font-medium ${hasGoogle ? 'text-text-primary' : 'text-text-muted'}`}>
                {hasGoogle && accountDetails.google.accounts.length > 0 && !accountDetails.google.accounts[0]?.needsReconnect && accountDetails.google.accounts[0]?.name !== 'Reconnection required'
                  ? `Google: ${accountDetails.google.accounts[0]?.email || accountDetails.google.accounts[0]?.name}`
                  : 'Google'
                }
              </span>
              <span className="text-xs text-text-secondary">
                ({accountDetails.google.count}/{accountDetails.google.max})
              </span>
            </div>
            {hasGoogle && accountDetails.google.accounts.length > 0 && (accountDetails.google.accounts[0]?.needsReconnect || accountDetails.google.accounts[0]?.name === 'Reconnection required') && (
              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                <div className="flex items-center gap-1 flex-wrap">
                  <AlertCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                  <span className="text-xs text-orange-600 font-medium">
                    Reconnection required
                  </span>
                  <button
                    type="button"
                    onClick={() => handleReconnect('google', memberId)}
                    className="text-xs text-orange-700 underline hover:text-orange-800 font-medium"
                  >
                    Reconnect now
                  </button>
                </div>
                {accountDetails.google.accounts[0]?.reconnectReason && (
                  <span className="text-xs text-text-secondary italic">
                    {accountDetails.google.accounts[0].reconnectReason}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Microsoft Status */}
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded flex items-center justify-center bg-white flex-shrink-0">
              <svg className="w-3 h-3" viewBox="0 0 24 24">
                <path fill="#F25022" d="M1 1h10v10H1z" />
                <path fill="#00A4EF" d="M1 13h10v10H1z" />
                <path fill="#7FBA00" d="M13 1h10v10H13z" />
                <path fill="#FFB900" d="M13 13h10v10H13z" />
              </svg>
            </div>
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              {hasMicrosoft ? (
                <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
              )}
              <span className={`text-xs font-medium ${hasMicrosoft ? 'text-text-primary' : 'text-text-muted'}`}>
                {hasMicrosoft && accountDetails.microsoft.accounts.length > 0 && !accountDetails.microsoft.accounts[0]?.needsReconnect && accountDetails.microsoft.accounts[0]?.name !== 'Reconnection required'
                  ? `Microsoft: ${accountDetails.microsoft.accounts[0]?.email || accountDetails.microsoft.accounts[0]?.name}`
                  : 'Microsoft'
                }
              </span>
              <span className="text-xs text-text-secondary">
                ({accountDetails.microsoft.count}/{accountDetails.microsoft.max})
              </span>
            </div>
            {hasMicrosoft && accountDetails.microsoft.accounts.length > 0 && (accountDetails.microsoft.accounts[0]?.needsReconnect || accountDetails.microsoft.accounts[0]?.name === 'Reconnection required') && (
              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                <div className="flex items-center gap-1 flex-wrap">
                  <AlertCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                  <span className="text-xs text-orange-600 font-medium">
                    Reconnection required
                  </span>
                  <button
                    type="button"
                    onClick={() => handleReconnect('microsoft', memberId)}
                    className="text-xs text-orange-700 underline hover:text-orange-800 font-medium"
                  >
                    Reconnect now
                  </button>
                </div>
                {accountDetails.microsoft.accounts[0]?.reconnectReason && (
                  <span className="text-xs text-text-secondary italic">
                    {accountDetails.microsoft.accounts[0].reconnectReason}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Reconnection buttons */}
          {(googleNeedsReconnect || microsoftNeedsReconnect) && (
            <div className="mt-3 pt-3 border-t border-border-light space-y-2">
              {googleNeedsReconnect && (
                <button
                  type="button"
                  onClick={() => handleReconnect('google', memberId)}
                  className="w-full px-3 py-2 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
                >
                  Reconnect Google Account
                </button>
              )}
              {microsoftNeedsReconnect && (
                <button
                  type="button"
                  onClick={() => handleReconnect('microsoft', memberId)}
                  className="w-full px-3 py-2 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
                >
                  Reconnect Microsoft Account
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full display mode (similar to InviteMemberModal)
  return (
    <div className="p-4 border border-border-default rounded-lg bg-bg-secondary">
      <h3 className="text-sm font-medium text-text-primary mb-3">Connected Accounts</h3>

      {/* Google Accounts */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-text-primary">Google Calendar</span>
          <span className="text-xs text-text-secondary">
            ({accountDetails.google.count}/{accountDetails.google.max})
          </span>
        </div>
        {accountDetails.google.accounts.length > 0 ? (
          <div className="space-y-1">
            {accountDetails.google.accounts.map((account, idx) => (
              <div key={idx} className="flex flex-col gap-1">
                {account.needsReconnect || account.name === 'Reconnection required' ? (
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <AlertCircle className="w-3 h-3 text-orange-500 flex-shrink-0" />
                      <span className="text-orange-600 font-medium">Reconnection required</span>
                      <button
                        type="button"
                        onClick={() => handleReconnect('google', memberId)}
                        className="text-xs text-orange-700 underline hover:text-orange-800 font-medium"
                      >
                        Reconnect now
                      </button>
                    </div>
                    {account.reconnectReason && (
                      <p className="text-xs text-text-secondary italic pl-5">
                        {account.reconnectReason}
                      </p>
                    )}
                    {account.email && (
                      <p className="text-xs text-text-muted pl-5">
                        Account: {account.email}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <span className="text-text-secondary">{account.email}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-text-secondary">No accounts connected</p>
        )}
      </div>

      {/* Microsoft Accounts */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-text-primary">Microsoft Calendar</span>
          <span className="text-xs text-text-secondary">
            ({accountDetails.microsoft.count}/{accountDetails.microsoft.max})
          </span>
        </div>
        {accountDetails.microsoft.accounts.length > 0 ? (
          <div className="space-y-1">
            {accountDetails.microsoft.accounts.map((account, idx) => (
              <div key={idx} className="flex flex-col gap-1">
                {account.needsReconnect || account.name === 'Reconnection required' ? (
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <AlertCircle className="w-3 h-3 text-orange-500 flex-shrink-0" />
                      <span className="text-orange-600 font-medium">Reconnection required</span>
                      <button
                        type="button"
                        onClick={() => handleReconnect('microsoft', memberId)}
                        className="text-xs text-orange-700 underline hover:text-orange-800 font-medium"
                      >
                        Reconnect now
                      </button>
                    </div>
                    {account.reconnectReason && (
                      <p className="text-xs text-text-secondary italic pl-5">
                        {account.reconnectReason}
                      </p>
                    )}
                    {account.email && (
                      <p className="text-xs text-text-muted pl-5">
                        Account: {account.email}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <span className="text-text-secondary">{account.email}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-text-secondary">No accounts connected</p>
        )}
      </div>

      {/* Reconnection buttons */}
      {(googleNeedsReconnect || microsoftNeedsReconnect) && (
        <div className="mt-4 pt-4 border-t border-border-light space-y-2">
          {googleNeedsReconnect && (
            <button
              type="button"
              onClick={() => handleReconnect('google', memberId)}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
            >
              Reconnect Google Account
            </button>
          )}
          {microsoftNeedsReconnect && (
            <button
              type="button"
              onClick={() => handleReconnect('microsoft', memberId)}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
            >
              Reconnect Microsoft Account
            </button>
          )}
        </div>
      )}
    </div>
  );
}
